import React, { useState, useEffect } from 'react';
import {
    Typography,
    Box,
    Paper,
    CircularProgress,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    IconButton,
    Grid,
    Divider,
    Chip,
    Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import http from '../../api/http';

interface LocalizedString {
    [key: string]: string;
}

interface PromQuestionOption {
    value: string;
    label: LocalizedString;
    requiresText: boolean;
}

interface PromQuestion {
    questionKey: string;
    question: LocalizedString;
    responseType: 'single' | 'multiple' | 'number' | 'nrs' | 'scale' | 'bsfs';
    options?: PromQuestionOption[];
    min?: number;
    max?: number;
    scaleLabels?: Record<string, string[]>;
    bsfsLabels?: Record<string, { type: string; desc: string }[]>;
    section?: string;
    order: number;
    active: boolean;
}

interface TranslationItem {
    id: string;
    lang: string;
    text: string;
}

interface EditableOption {
    value: string;
    labels: TranslationItem[];
    requiresText: boolean;
}

interface EditableForm {
    questionKey: string;
    questionTranslations: TranslationItem[];
    responseType: 'single' | 'multiple' | 'number' | 'nrs' | 'scale' | 'bsfs';
    options: EditableOption[];
    min?: number;
    max?: number;
    scaleLabels?: string[];
    scaleLabelLangs?: string[];
    bsfsLabels: { type: string; desc: string }[];
    section: string;
    order: number;
    required: boolean;
    active: boolean;
}

const SECTION_OPTIONS = [
    { value: '', label: 'Unassigned' },
    { value: 'demographics', label: 'Demographics' },
    { value: 'lifestyle', label: 'Lifestyle' },
    { value: 'who5', label: 'WHO-5' },
    { value: 'phq2_gad2', label: 'PHQ-2/GAD-2' },
    { value: 'energy_sleep', label: 'Energy & Sleep' },
    { value: 'body_stress', label: 'Body & Stress' },
    { value: 'digestive', label: 'Digestive' },
];

const RESPONSE_TYPE_OPTIONS = [
    { value: 'single', label: 'Single Choice' },
    { value: 'multiple', label: 'Multiple Choice' },
    { value: 'number', label: 'Number' },
    { value: 'nrs', label: 'NRS' },
    { value: 'scale', label: 'Scale' },
    { value: 'bsfs', label: 'BSFS' },
];

const RESPONSE_TYPE_COLORS: Record<string, string> = {
    single: '#9c27b0',
    multiple: '#2196f3',
    number: '#4caf50',
    nrs: '#ff9800',
    scale: '#00bcd4',
    bsfs: '#f44336',
};

const PromAdminPage = () => {
    const [questions, setQuestions] = useState<PromQuestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingForm, setEditingForm] = useState<EditableForm | null>(null);
    const [saving, setSaving] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [sectionFilter, setSectionFilter] = useState('all');

    const SECTION_FILTER_OPTIONS = [
        { value: 'all', label: 'All' },
        ...SECTION_OPTIONS.filter(s => s.value !== ''),
    ];

    const fetchQuestions = async () => {
        setLoading(true);
        try {
            const response = await http.get('/api/prom/questions');
            if (response.data && response.data.success) {
                const grouped = response.data.data;
                const flat: PromQuestion[] = [];
                for (const section of Object.values(grouped)) {
                    if (Array.isArray(section)) {
                        flat.push(...section);
                    }
                }
                setQuestions(flat);
            } else {
                setError('Failed to load PROM questions');
            }
        } catch (err) {
            console.error('Error fetching PROM questions:', err);
            setError('An error occurred while fetching PROM questions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQuestions();
    }, []);

    const transformToEditable = (question: PromQuestion): EditableForm => {
        return {
            questionKey: question.questionKey,
            questionTranslations: Object.entries(question.question).map(([lang, text]) => ({
                id: `${lang}-${Date.now()}-${Math.random()}`,
                lang,
                text
            })),
            responseType: question.responseType,
            options: (question.options || []).map(opt => ({
                value: opt.value,
                labels: Object.entries(opt.label).map(([lang, text]) => ({
                    id: `${lang}-${Date.now()}-${Math.random()}`,
                    lang,
                    text
                })),
                requiresText: opt.requiresText
            })),
            min: question.min,
            max: question.max,
            scaleLabels: question.scaleLabels ? Object.entries(question.scaleLabels).flatMap(([, labels]) => labels) : undefined,
            scaleLabelLangs: question.scaleLabels ? Object.keys(question.scaleLabels) : undefined,
            bsfsLabels: question.bsfsLabels ? Object.entries(question.bsfsLabels).flatMap(([, labels]) => labels) : [],
            section: question.section || '',
            order: question.order,
            required: false,
            active: question.active
        };
    };

    const transformFromEditable = (form: EditableForm): Partial<PromQuestion> => {
        const question: LocalizedString = {};
        form.questionTranslations.forEach(t => {
            if (t.lang) question[t.lang] = t.text;
        });

        const options: PromQuestionOption[] = form.options.map(opt => {
            const label: LocalizedString = {};
            opt.labels.forEach(t => {
                if (t.lang) label[t.lang] = t.text;
            });
            return {
                value: opt.value,
                label,
                requiresText: opt.requiresText
            };
        });

        const scaleLabels: Record<string, string[]> = {};
        if (form.scaleLabels) {
            const langs = form.scaleLabelLangs || ['en', 'de'];
            if (langs.length > 0) {
                const len = form.scaleLabels.length;
                const perLang = Math.floor(len / langs.length);
                for (let i = 0; i < langs.length; i++) {
                    scaleLabels[langs[i]] = form.scaleLabels.slice(perLang * i, perLang * (i + 1));
                }
            }
        }

        const bsfsLabels: Record<string, { type: string; desc: string }[]> = {};
        if (form.bsfsLabels && form.bsfsLabels.length > 0) {
            bsfsLabels['en'] = form.bsfsLabels;
        }

        return {
            questionKey: form.questionKey,
            question,
            responseType: form.responseType,
            options: form.responseType === 'single' || form.responseType === 'multiple' ? options : undefined,
            min: form.responseType === 'number' || form.responseType === 'nrs' || form.responseType === 'scale' ? form.min : undefined,
            max: form.responseType === 'number' || form.responseType === 'nrs' || form.responseType === 'scale' ? form.max : undefined,
            scaleLabels: form.responseType === 'scale' ? scaleLabels : undefined,
            bsfsLabels: form.responseType === 'bsfs' ? bsfsLabels : undefined,
            section: form.section || undefined,
            order: form.order,
            active: form.active,
        };
    };

    const handleEditClick = (question: PromQuestion) => {
        setEditingForm(transformToEditable(question));
        setIsCreating(false);
        setEditDialogOpen(true);
    };

    const handleCreateClick = () => {
        setEditingForm({
            questionKey: '',
            questionTranslations: [{ id: `en-${Date.now()}`, lang: 'en', text: '' }],
            responseType: 'single',
            options: [],
            min: 0,
            max: 10,
            bsfsLabels: [],
            section: '',
            order: 0,
            scaleLabelLangs: ['en', 'de'],
            required: false,
            active: true
        });
        setIsCreating(true);
        setEditDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setEditDialogOpen(false);
        setEditingForm(null);
    };

    const handleSave = async () => {
        if (!editingForm) return;

        setSaving(true);
        try {
            const payload = transformFromEditable(editingForm);
            let response: any;

            if (isCreating) {
                response = await http.post('/api/prom/questions', payload);
            } else {
                response = await http.put(`/api/prom/questions/${editingForm.questionKey}`, payload);
            }

            if (response.data && response.data.success) {
                fetchQuestions();
                handleCloseDialog();
            } else {
                alert('Failed to save changes');
            }
        } catch (err) {
            console.error('Error saving PROM question:', err);
            alert('An error occurred while saving');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (questionKey: string) => {
        if (!window.confirm('Are you sure you want to delete this PROM question?')) {
            return;
        }

        try {
            const response = await http.remove(`/api/prom/questions/${questionKey}`, {});
            if (response.data && response.data.success) {
                setQuestions(prev => prev.filter(q => q.questionKey !== questionKey));
            } else {
                alert('Failed to delete PROM question');
            }
        } catch (err) {
            console.error('Error deleting PROM question:', err);
            alert('An error occurred while deleting');
        }
    };

    const handleAddQuestionTranslation = () => {
        if (!editingForm) return;
        setEditingForm({
            ...editingForm,
            questionTranslations: [
                ...editingForm.questionTranslations,
                { id: `new-${Date.now()}`, lang: '', text: '' }
            ]
        });
    };

    const handleRemoveQuestionTranslation = (id: string) => {
        if (!editingForm) return;
        setEditingForm({
            ...editingForm,
            questionTranslations: editingForm.questionTranslations.filter(t => t.id !== id)
        });
    };

    const handleQuestionTranslationChange = (id: string, field: 'lang' | 'text', value: string) => {
        if (!editingForm) return;
        setEditingForm({
            ...editingForm,
            questionTranslations: editingForm.questionTranslations.map(t =>
                t.id === id ? { ...t, [field]: value } : t
            )
        });
    };

    const handleAddOption = () => {
        if (!editingForm) return;
        setEditingForm({
            ...editingForm,
            options: [
                ...editingForm.options,
                {
                    value: `option_${Date.now()}`,
                    labels: [{ id: `en-${Date.now()}`, lang: 'en', text: '' }],
                    requiresText: false
                }
            ]
        });
    };

    const handleRemoveOption = (index: number) => {
        if (!editingForm) return;
        setEditingForm({
            ...editingForm,
            options: editingForm.options.filter((_, i) => i !== index)
        });
    };

    const handleOptionValueChange = (index: number, value: string) => {
        if (!editingForm) return;
        const newOptions = [...editingForm.options];
        newOptions[index].value = value;
        setEditingForm({ ...editingForm, options: newOptions });
    };

    const handleOptionRequiresTextChange = (index: number, checked: boolean) => {
        if (!editingForm) return;
        const newOptions = [...editingForm.options];
        newOptions[index].requiresText = checked;
        setEditingForm({ ...editingForm, options: newOptions });
    };

    const handleAddOptionLabel = (optionIndex: number) => {
        if (!editingForm) return;
        const newOptions = [...editingForm.options];
        newOptions[optionIndex].labels.push({
            id: `new-${Date.now()}`,
            lang: '',
            text: ''
        });
        setEditingForm({ ...editingForm, options: newOptions });
    };

    const handleRemoveOptionLabel = (optionIndex: number, labelId: string) => {
        if (!editingForm) return;
        const newOptions = [...editingForm.options];
        newOptions[optionIndex].labels = newOptions[optionIndex].labels.filter(l => l.id !== labelId);
        setEditingForm({ ...editingForm, options: newOptions });
    };

    const handleOptionLabelChange = (optionIndex: number, labelId: string, field: 'lang' | 'text', value: string) => {
        if (!editingForm) return;
        const newOptions = [...editingForm.options];
        newOptions[optionIndex].labels = newOptions[optionIndex].labels.map(l =>
            l.id === labelId ? { ...l, [field]: value } : l
        );
        setEditingForm({ ...editingForm, options: newOptions });
    };

    const handleAddBsfsLabel = () => {
        if (!editingForm) return;
        setEditingForm({
            ...editingForm,
            bsfsLabels: [...editingForm.bsfsLabels, { type: '', desc: '' }]
        });
    };

    const handleRemoveBsfsLabel = (index: number) => {
        if (!editingForm) return;
        setEditingForm({
            ...editingForm,
            bsfsLabels: editingForm.bsfsLabels.filter((_, i) => i !== index)
        });
    };

    const handleBsfsLabelChange = (index: number, field: 'type' | 'desc', value: string) => {
        if (!editingForm) return;
        const newBsfsLabels = [...editingForm.bsfsLabels];
        newBsfsLabels[index] = { ...newBsfsLabels[index], [field]: value };
        setEditingForm({ ...editingForm, bsfsLabels: newBsfsLabels });
    };

    const handleAddScaleLabel = () => {
        if (!editingForm) return;
        setEditingForm({
            ...editingForm,
            scaleLabels: [...(editingForm.scaleLabels || []), '']
        });
    };

    const handleRemoveScaleLabel = (index: number) => {
        if (!editingForm) return;
        setEditingForm({
            ...editingForm,
            scaleLabels: (editingForm.scaleLabels || []).filter((_, i) => i !== index)
        });
    };

    const filteredQuestions = sectionFilter === 'all'
        ? questions
        : questions.filter(q => q.section === sectionFilter);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4">
                    Prom Questions
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateClick}>
                    Create Question
                </Button>
            </Box>

            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">Section:</Typography>
                <FormControl sx={{ minWidth: 200 }}>
                    <Select
                        value={sectionFilter}
                        label="Section"
                        onChange={(e) => setSectionFilter(e.target.value)}
                    >
                        {SECTION_FILTER_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                    {filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''}
                </Typography>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Order</TableCell>
                            <TableCell>Key</TableCell>
                            <TableCell>Question (EN / DE)</TableCell>
                            <TableCell>Response Type</TableCell>
                            <TableCell>Section</TableCell>
                            <TableCell>Active</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredQuestions.sort((a, b) => a.order - b.order).map((q) => (
                            <TableRow key={q.questionKey}>
                                <TableCell>{q.order}</TableCell>
                                <TableCell>{q.questionKey}</TableCell>
                                <TableCell>
                                    <Typography variant="body2">{q.question['en'] || Object.values(q.question)[0] || ''}</Typography>
                                    {q.question['de'] && (
                                        <Typography variant="caption" color="text.secondary">{q.question['de']}</Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={q.responseType}
                                        size="small"
                                        sx={{
                                            backgroundColor: RESPONSE_TYPE_COLORS[q.responseType] || '#757575',
                                            color: '#fff',
                                            fontWeight: 500,
                                        }}
                                    />
                                </TableCell>
                                <TableCell>{q.section || '-'}</TableCell>
                                <TableCell>{q.active ? 'Yes' : 'No'}</TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={1}>
                                        <Button size="small" startIcon={<EditIcon />} onClick={() => handleEditClick(q)}>
                                            Edit
                                        </Button>
                                        <Button size="small" startIcon={<DeleteIcon />} color="error" onClick={() => handleDelete(q.questionKey)}>
                                            Delete
                                        </Button>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={editDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {isCreating ? 'Create New PROM Question' : `Edit Question: ${editingForm?.questionKey}`}
                </DialogTitle>
                <DialogContent>
                    {editingForm && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        label="Question Key"
                                        fullWidth
                                        value={editingForm.questionKey}
                                        onChange={(e) => setEditingForm({ ...editingForm, questionKey: e.target.value })}
                                        disabled={!isCreating}
                                        helperText={isCreating ? "Unique identifier for the question (e.g., 'WHO5_1')" : "Cannot be changed after creation"}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <FormControl fullWidth>
                                        <InputLabel>Response Type</InputLabel>
                                        <Select
                                            value={editingForm.responseType}
                                            label="Response Type"
                                            onChange={(e) => setEditingForm({
                                                ...editingForm,
                                                responseType: e.target.value as EditableForm['responseType'],
                                                options: (e.target.value === 'single' || e.target.value === 'multiple') ? editingForm.options : [],
                                                min: (e.target.value === 'number' || e.target.value === 'nrs' || e.target.value === 'scale') ? editingForm.min : undefined,
                                                max: (e.target.value === 'number' || e.target.value === 'nrs' || e.target.value === 'scale') ? editingForm.max : undefined,
                                                scaleLabels: e.target.value === 'scale' ? editingForm.scaleLabels : undefined,
                                                bsfsLabels: e.target.value === 'bsfs' ? editingForm.bsfsLabels : [],
                                            })}
                                        >
                                            {RESPONSE_TYPE_OPTIONS.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <FormControl fullWidth>
                                        <InputLabel>Section</InputLabel>
                                        <Select
                                            value={editingForm.section}
                                            label="Section"
                                            onChange={(e) => setEditingForm({ ...editingForm, section: e.target.value })}
                                        >
                                            {SECTION_OPTIONS.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <TextField
                                        label="Order"
                                        type="number"
                                        fullWidth
                                        value={editingForm.order}
                                        onChange={(e) => setEditingForm({ ...editingForm, order: Number(e.target.value) })}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={editingForm.active}
                                                onChange={(e) => setEditingForm({ ...editingForm, active: e.target.checked })}
                                            />
                                        }
                                        label="Active"
                                    />
                                </Grid>
                            </Grid>

                            {editingForm.responseType === 'number' || editingForm.responseType === 'nrs' || editingForm.responseType === 'scale' ? (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle1">Range</Typography>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 6 }}>
                                            <TextField
                                                label="Min"
                                                type="number"
                                                fullWidth
                                                value={editingForm.min ?? ''}
                                                onChange={(e) => setEditingForm({ ...editingForm, min: Number(e.target.value) })}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <TextField
                                                label="Max"
                                                type="number"
                                                fullWidth
                                                value={editingForm.max ?? ''}
                                                onChange={(e) => setEditingForm({ ...editingForm, max: Number(e.target.value) })}
                                            />
                                        </Grid>
                                    </Grid>
                                </>
                            ) : null}

                            {editingForm.responseType === 'scale' ? (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle1">Scale Labels</Typography>
                                        <Button startIcon={<AddIcon />} size="small" onClick={handleAddScaleLabel}>
                                            Add Label
                                        </Button>
                                    </Box>
                                    {editingForm.scaleLabels?.map((label, index) => (
                                        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 30 }}>
                                                {index + 1}.
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder={`Label for position ${index + 1}`}
                                                value={label}
                                                onChange={(e) => {
                                                    const newLabels = [...(editingForm.scaleLabels || [])];
                                                    newLabels[index] = e.target.value;
                                                    setEditingForm({ ...editingForm, scaleLabels: newLabels });
                                                }}
                                            />
                                            <IconButton size="small" color="error" onClick={() => handleRemoveScaleLabel(index)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </>
                            ) : null}

                            {editingForm.responseType === 'bsfs' ? (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle1">Bristol Stool Scale Labels</Typography>
                                        <Button startIcon={<AddIcon />} size="small" onClick={handleAddBsfsLabel}>
                                            Add Type
                                        </Button>
                                    </Box>
                                    {editingForm.bsfsLabels.map((label, index) => (
                                        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 30 }}>
                                                {index + 1}.
                                            </Typography>
                                            <TextField
                                                label="Type"
                                                size="small"
                                                value={label.type}
                                                onChange={(e) => handleBsfsLabelChange(index, 'type', e.target.value)}
                                                sx={{ width: '80px' }}
                                            />
                                            <TextField
                                                label="Description"
                                                size="small"
                                                fullWidth
                                                value={label.desc}
                                                onChange={(e) => handleBsfsLabelChange(index, 'desc', e.target.value)}
                                            />
                                            <IconButton size="small" color="error" onClick={() => handleRemoveBsfsLabel(index)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </>
                            ) : null}

                            {editingForm.responseType === 'single' || editingForm.responseType === 'multiple' ? (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle1">Options</Typography>
                                        <Button startIcon={<AddIcon />} onClick={handleAddOption}>
                                            Add Option
                                        </Button>
                                    </Box>
                                    {editingForm.options.map((option, index) => (
                                        <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                                            <Grid container spacing={2}>
                                                <Grid size={{ xs: 12, sm: 3 }}>
                                                    <TextField
                                                        label="Value"
                                                        fullWidth
                                                        size="small"
                                                        value={option.value}
                                                        onChange={(e) => handleOptionValueChange(index, e.target.value)}
                                                        sx={{ mb: 2 }}
                                                    />
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                size="small"
                                                                checked={option.requiresText}
                                                                onChange={(e) => handleOptionRequiresTextChange(index, e.target.checked)}
                                                            />
                                                        }
                                                        label="Text Input"
                                                    />
                                                    <Box sx={{ mt: 2 }}>
                                                        <Button
                                                            startIcon={<DeleteIcon />}
                                                            color="error"
                                                            size="small"
                                                            onClick={() => handleRemoveOption(index)}
                                                        >
                                                            Remove Option
                                                        </Button>
                                                    </Box>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 9 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                        <Typography variant="caption" color="text.secondary">Labels</Typography>
                                                        <Button startIcon={<AddIcon />} size="small" onClick={() => handleAddOptionLabel(index)}>
                                                            Add Label
                                                        </Button>
                                                    </Box>
                                                    {option.labels.map((label) => (
                                                        <Box key={label.id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                                            <TextField
                                                                label="Lang"
                                                                size="small"
                                                                value={label.lang}
                                                                onChange={(e) => handleOptionLabelChange(index, label.id, 'lang', e.target.value)}
                                                                sx={{ width: '80px' }}
                                                            />
                                                            <TextField
                                                                label="Label Text"
                                                                size="small"
                                                                fullWidth
                                                                value={label.text}
                                                                onChange={(e) => handleOptionLabelChange(index, label.id, 'text', e.target.value)}
                                                            />
                                                            <IconButton size="small" color="error" onClick={() => handleRemoveOptionLabel(index, label.id)}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    ))}
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    ))}
                                </>
                            ) : null}

                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle1">Question Translations</Typography>
                                <Button startIcon={<AddIcon />} size="small" onClick={handleAddQuestionTranslation}>
                                    Add Language
                                </Button>
                            </Box>
                            {editingForm.questionTranslations.map((trans) => (
                                <Box key={trans.id} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                    <TextField
                                        label="Lang"
                                        size="small"
                                        value={trans.lang}
                                        onChange={(e) => handleQuestionTranslationChange(trans.id, 'lang', e.target.value)}
                                        sx={{ width: '100px' }}
                                        placeholder="en"
                                    />
                                    <TextField
                                        label="Text"
                                        size="small"
                                        fullWidth
                                        value={trans.text}
                                        onChange={(e) => handleQuestionTranslationChange(trans.id, 'text', e.target.value)}
                                    />
                                    <IconButton color="error" onClick={() => handleRemoveQuestionTranslation(trans.id)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PromAdminPage;
